/**
 * Écriture d'un classeur Excel (.xlsx) côté navigateur.
 *
 * Un .xlsx est une archive ZIP de quelques fichiers XML (SpreadsheetML). On n'en écrit que le
 * strict nécessaire — une feuille par jeu de données, une ligne d'en-tête figée et filtrable,
 * des nombres en nombres et des dates en vraies dates Excel — plutôt que d'embarquer une
 * bibliothèque de tableur de plusieurs centaines de kilo-octets. Comme pour le CSV, aucune
 * donnée ne repasse par le serveur.
 *
 * Les dates sont écrites dans le fuseau d'affichage du back-office, celui des écrans : une
 * heure lue dans le tableau doit être la même que dans Excel.
 */

import { strToU8, zipSync } from 'fflate';

import { FUSEAU_AFFICHAGE } from '../config';

export type CelluleTableur = string | number | boolean | Date | null | undefined;

export interface FeuilleTableur {
  nom: string;
  entetes: readonly string[];
  lignes: ReadonlyArray<ReadonlyArray<CelluleTableur>>;
}

/** Limite d'Excel pour le texte d'une cellule. */
const LONGUEUR_MAX_CELLULE = 32_767;

/** Styles déclarés dans `styles.xml`, par rang dans `cellXfs`. */
const STYLE_DATE = 1;
const STYLE_ENTETE = 2;

const partiesDate = new Intl.DateTimeFormat('en-GB', {
  timeZone: FUSEAU_AFFICHAGE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Numéro de série Excel (jours depuis le 30/12/1899) de l'heure murale du fuseau d'affichage. */
function dateExcel(date: Date): number {
  const p: Record<string, number> = {};
  for (const partie of partiesDate.formatToParts(date)) {
    if (partie.type !== 'literal') p[partie.type] = Number(partie.value);
  }
  const murale = Date.UTC(
    p['year'] ?? 1970,
    (p['month'] ?? 1) - 1,
    p['day'] ?? 1,
    p['hour'] ?? 0,
    p['minute'] ?? 0,
    p['second'] ?? 0,
    date.getUTCMilliseconds(),
  );
  return murale / 86_400_000 + 25_569;
}

function echapperXml(texte: string): string {
  return texte
    // Caractères de contrôle interdits en XML 1.0 (hors tabulation et fins de ligne).
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/gu, '')
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;');
}

/** `0` → `A`, `26` → `AA`. */
function lettreColonne(index: number): string {
  let n = index + 1;
  let lettres = '';
  while (n > 0) {
    const reste = (n - 1) % 26;
    lettres = String.fromCharCode(65 + reste) + lettres;
    n = Math.floor((n - 1) / 26);
  }
  return lettres;
}

function cellule(ref: string, valeur: CelluleTableur, style?: number): string {
  if (valeur === null || valeur === undefined || valeur === '') return '';
  const s = style === undefined ? '' : ` s="${style}"`;
  if (valeur instanceof Date) {
    return Number.isNaN(valeur.getTime()) ? '' : `<c r="${ref}" s="${STYLE_DATE}"><v>${dateExcel(valeur)}</v></c>`;
  }
  if (typeof valeur === 'number') {
    return Number.isFinite(valeur) ? `<c r="${ref}"${s}><v>${valeur}</v></c>` : '';
  }
  if (typeof valeur === 'boolean') {
    return `<c r="${ref}" t="b"${s}><v>${valeur ? 1 : 0}</v></c>`;
  }
  const texte = valeur.length > LONGUEUR_MAX_CELLULE ? valeur.slice(0, LONGUEUR_MAX_CELLULE) : valeur;
  return `<c r="${ref}" t="inlineStr"${s}><is><t xml:space="preserve">${echapperXml(texte)}</t></is></c>`;
}

/** Largeur de colonne estimée sur l'en-tête et les premières lignes, bornée. */
function largeurs(feuille: FeuilleTableur): number[] {
  return feuille.entetes.map((entete, i) => {
    let max = entete.length;
    for (const ligne of feuille.lignes.slice(0, 200)) {
      const v = ligne[i];
      const longueur = v instanceof Date ? 23 : v === null || v === undefined ? 0 : String(v).length;
      if (longueur > max) max = longueur;
    }
    return Math.min(Math.max(max + 2, 8), 80);
  });
}

function xmlFeuille(feuille: FeuilleTableur): string {
  const rangs: string[] = [];
  rangs.push(
    `<row r="1">${feuille.entetes.map((e, i) => cellule(`${lettreColonne(i)}1`, e, STYLE_ENTETE)).join('')}</row>`,
  );
  feuille.lignes.forEach((ligne, r) => {
    const numero = r + 2;
    rangs.push(
      `<row r="${numero}">${ligne.map((v, i) => cellule(`${lettreColonne(i)}${numero}`, v)).join('')}</row>`,
    );
  });
  const derniere = lettreColonne(Math.max(feuille.entetes.length - 1, 0));
  const colonnes = largeurs(feuille)
    .map((l, i) => `<col min="${i + 1}" max="${i + 1}" width="${l}" customWidth="1"/>`)
    .join('');
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' +
    `<cols>${colonnes}</cols>` +
    `<sheetData>${rangs.join('')}</sheetData>` +
    `<autoFilter ref="A1:${derniere}${feuille.lignes.length + 1}"/>` +
    '</worksheet>'
  );
}

const STYLES =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<numFmts count="1"><numFmt numFmtId="164" formatCode="dd/mm/yyyy hh:mm:ss.000"/></numFmts>' +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="3">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  '</cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  '</styleSheet>';

/** Nom de feuille valide pour Excel : 31 caractères, sans `[]:*?/\`. */
function nomFeuille(nom: string, index: number): string {
  const propre = nom.replace(/[[\]:*?/\\]/gu, ' ').trim().slice(0, 31);
  return propre === '' ? `Feuille ${index + 1}` : propre;
}

/** Construit le classeur, prêt à être téléchargé. */
export function construireXlsx(feuilles: readonly FeuilleTableur[]): Uint8Array {
  const fichiers: Record<string, Uint8Array> = {};

  fichiers['[Content_Types].xml'] = strToU8(
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
      '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
      '<Default Extension="xml" ContentType="application/xml"/>' +
      '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
      '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
      feuilles
        .map(
          (_, i) =>
            `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
        )
        .join('') +
      '</Types>',
  );

  fichiers['_rels/.rels'] = strToU8(
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
      '</Relationships>',
  );

  fichiers['xl/workbook.xml'] = strToU8(
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
      `<sheets>${feuilles
        .map((f, i) => `<sheet name="${echapperXml(nomFeuille(f.nom, i))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
        .join('')}</sheets>` +
      // Plage nommée exigée par Excel pour que le filtre automatique de chaque feuille tienne.
      `<definedNames>${feuilles
        .map(
          (f, i) =>
            `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">'${echapperXml(nomFeuille(f.nom, i)).replace(/'/gu, "''")}'!$A$1:$${lettreColonne(Math.max(f.entetes.length - 1, 0))}$${f.lignes.length + 1}</definedName>`,
        )
        .join('')}</definedNames>` +
      '</workbook>',
  );

  fichiers['xl/_rels/workbook.xml.rels'] = strToU8(
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
      feuilles
        .map(
          (_, i) =>
            `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
        )
        .join('') +
      `<Relationship Id="rId${feuilles.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
      '</Relationships>',
  );

  fichiers['xl/styles.xml'] = strToU8(STYLES);
  feuilles.forEach((f, i) => {
    fichiers[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(xmlFeuille(f));
  });

  return zipSync(fichiers, { level: 6 });
}

/** Déclenche le téléchargement d'un classeur. */
export function telechargerXlsx(nomFichier: string, feuilles: readonly FeuilleTableur[]): void {
  const octets = construireXlsx(feuilles);
  const blob = new Blob([octets as Uint8Array<ArrayBuffer>], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}
